import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { format, parseISO, startOfDay, isSameDay, setHours, setMinutes } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { RecurringEventActionModal } from './RecurringEventActionModal';
import ViewEventModal from './ViewEventModal';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { UserAvatar } from '../common/UserAvatar';

export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
  practice_id?: string; // Match cached type
  photo_url?: string;
}

interface VetAvailability {
  id: string;
  vet_user_id: string;
  practice_id: string;
  start_at: string;
  end_at: string;
  availability_type: 'AVAILABLE' | 'SURGERY_BLOCK' | 'UNAVAILABLE' | 'EMERGENCY_ONLY';
  title?: string;
  notes?: string;
  is_active: boolean;
  is_recurring?: boolean;
  recurring_template_id?: string;
}

interface TimeSlot {
  time: Date;
  label: string;
}

interface WorkScheduleSwimLanesViewProps {
  scheduleEvents: VetAvailability[];
  onSelectEvent: (eventId: string) => void;
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onRescheduleEvent?: (eventId: string, start: Date, end: Date) => void;
  onRefresh?: () => Promise<void>;
  onSelectSlot?: (slotInfo: { start: Date; end: Date; vetId?: string }) => void;
  selectedVetIds?: Set<string>;
  teamMembers: TeamMember[]; // REQUIRED: passed from Dashboard
}

const SLOT_MINUTES = 15;
const SLOT_HEIGHT_PX = 32; // 32px per 15 minutes (matches previous density: 64px per 30 minutes)

type HoursMode = 'business' | '24h';
const HOURS_MODE_STORAGE_KEY = 'hp_swimlane_hours_mode';
type PracticeHoursRow = { day?: string; open?: string; close?: string; closed?: boolean };

const parseHour = (hhmm?: string | null): { hour: number; minute: number } | null => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { hour: h, minute: m };
};

const roundDownToSlot = (minutes: number) => Math.floor(minutes / SLOT_MINUTES) * SLOT_MINUTES;
const roundUpToSlot = (minutes: number) => Math.ceil(minutes / SLOT_MINUTES) * SLOT_MINUTES;

const businessHoursForDate = (hours: PracticeHoursRow[] | null | undefined, date: Date) => {
  const fallback = { startMin: 8 * 60, endMinExclusive: 18 * 60 };
  if (!hours || !Array.isArray(hours) || hours.length === 0) return fallback;

  const day = format(date, 'EEEE').toLowerCase();
  const row = hours.find((r) => (r?.day || '').toLowerCase() === day);
  if (!row || row.closed) return fallback;

  const open = parseHour(row.open);
  const close = parseHour(row.close);
  if (!open || !close) return fallback;

  const startMin = Math.max(0, Math.min(23 * 60 + 59, open.hour * 60 + open.minute));
  const endMin = Math.max(0, Math.min(24 * 60, close.hour * 60 + close.minute));

  const snappedStart = roundDownToSlot(startMin);
  let snappedEndExclusive = roundUpToSlot(endMin);
  if (snappedEndExclusive <= snappedStart) snappedEndExclusive = snappedStart + SLOT_MINUTES;
  if (snappedEndExclusive > 24 * 60) snappedEndExclusive = 24 * 60;

  return { startMin: snappedStart, endMinExclusive: snappedEndExclusive };
};

// Generate 15-minute time slots between [startMin, endMinExclusive)
const generateTimeSlots = (date: Date, startMin: number, endMinExclusive: number): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  for (let m = startMin; m < endMinExclusive; m += SLOT_MINUTES) {
    const slotTime = setMinutes(setHours(new Date(date), Math.floor(m / 60)), m % 60);
    const showLabel = m % 60 === 0 || m === startMin;
    slots.push({ time: slotTime, label: showLabel ? format(slotTime, 'h:mm a') : '' });
  }

  return slots;
};

// Get doctor color based on their index in team members (matching avatars)
const getDoctorColor = (vetUserId: string, teamMembers: TeamMember[]): { bg: string; border: string } => {
  const colors = [
    { bg: 'bg-blue-500', border: 'border-blue-600' },      // blue
    { bg: 'bg-emerald-500', border: 'border-emerald-600' }, // emerald
    { bg: 'bg-purple-500', border: 'border-purple-600' },   // purple
    { bg: 'bg-orange-500', border: 'border-orange-600' },   // orange
    { bg: 'bg-pink-500', border: 'border-pink-600' },       // pink
    { bg: 'bg-indigo-500', border: 'border-indigo-600' },   // indigo
    { bg: 'bg-teal-500', border: 'border-teal-600' },       // teal
    { bg: 'bg-rose-500', border: 'border-rose-600' },       // rose
  ];

  const index = teamMembers.findIndex(member => member.id === vetUserId);
  if (index === -1) return colors[0]; // Default to blue if not found

  return colors[index % colors.length];
};

// Get opacity class based on availability type
const getAvailabilityOpacity = (availabilityType: string): string => {
  switch (availabilityType) {
    case 'AVAILABLE':
      return 'opacity-95'; // Full opacity
    case 'SURGERY_BLOCK':
      return 'opacity-85'; // Slightly less
    case 'UNAVAILABLE':
      return 'opacity-50'; // More transparent
    case 'EMERGENCY_ONLY':
      return 'opacity-75'; // Medium transparency
    default:
      return 'opacity-95';
  }
};

// Get availability type label
const getAvailabilityLabel = (availabilityType: string): string => {
  switch (availabilityType) {
    case 'AVAILABLE':
      return 'Available';
    case 'SURGERY_BLOCK':
      return 'Surgery Block';
    case 'UNAVAILABLE':
      return 'Unavailable';
    case 'EMERGENCY_ONLY':
      return 'Emergency Only';
    default:
      return availabilityType;
  }
};

const WorkScheduleSwimLanesView: React.FC<WorkScheduleSwimLanesViewProps> = ({
  scheduleEvents,
  onSelectEvent,
  currentDate,
  onNavigate,
  onRescheduleEvent,
  onRefresh,
  onSelectSlot,
  selectedVetIds: externalSelectedVetIds,
  teamMembers, // PASSED FROM DASHBOARD - no fetching needed
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false); // No loading state needed since we get teamMembers from props
  const [draggedEvent, setDraggedEvent] = useState<VetAvailability | null>(null);
  const [dragOverVet, setDragOverVet] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const gridRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [hoursMode, setHoursMode] = useState<HoursMode>(() => {
    const saved = localStorage.getItem(HOURS_MODE_STORAGE_KEY) as HoursMode | null;
    return saved === '24h' ? '24h' : 'business';
  });

  const [practiceHours, setPracticeHours] = useState<PracticeHoursRow[] | null>(null);
  useEffect(() => {
    const loadPractice = async () => {
      try {
        if (!user?.practice_id) return;
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) return;
        const resp = await fetch(`${API_BASE_URL}/api/v1/practices/${user.practice_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (Array.isArray(data?.hours)) {
          setPracticeHours(data.hours);
          console.info('[WorkSchedule][PracticeHours] Loaded', {
            practice_id: user.practice_id,
            date: format(currentDate, 'yyyy-MM-dd'),
            hours: data.hours,
          });
        } else {
          console.info('[WorkSchedule][PracticeHours] No hours on practice response', {
            practice_id: user.practice_id,
            date: format(currentDate, 'yyyy-MM-dd'),
            hours: data?.hours,
          });
        }
      } catch {
        // ignore
      }
    };
    loadPractice();
  }, [user?.practice_id, currentDate]);

  // React immediately when Appointments toggles the mode (no remount required)
  useEffect(() => {
    const handler = (e: any) => {
      const next = e?.detail?.mode as HoursMode | undefined;
      if (next === '24h' || next === 'business') {
        setHoursMode(next);
      }
    };
    window.addEventListener('hp_swimlane_hours_mode_changed', handler);
    return () => window.removeEventListener('hp_swimlane_hours_mode_changed', handler);
  }, []);

  // Use external selectedVetIds if provided, otherwise show all
  const selectedVetIds = externalSelectedVetIds || new Set<string>();

  // State for drag-to-create new availability blocks
  const [isCreatingSlot, setIsCreatingSlot] = useState(false);
  const [slotCreationStart, setSlotCreationStart] = useState<{ vetId: string, slotTime: Date } | null>(null);
  const [slotCreationEnd, setSlotCreationEnd] = useState<{ vetId: string, slotTime: Date } | null>(null);

  // State for view event modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEventForView, setSelectedEventForView] = useState<VetAvailability | null>(null);

  // State for recurring event action modal
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringModalAction, setRecurringModalAction] = useState<'edit' | 'delete' | 'move'>('edit');
  const [pendingRecurringAction, setPendingRecurringAction] = useState<{
    event: VetAvailability;
    newStartTime?: Date;
    newEndTime?: Date;
  } | null>(null);

  // State for confirm delete modal (non-recurring events)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add global mouseup handler to catch releases outside the grid
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isCreatingSlot) {
        handleSlotMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isCreatingSlot, slotCreationStart, slotCreationEnd]);


  // Filter schedule events for the current day
  const todaySchedule = useMemo(() => {
    return scheduleEvents.filter(event => {
      try {
        const eventDate = parseISO(event.start_at);
        return isSameDay(eventDate, currentDate);
      } catch (error) {
        console.error('Error parsing schedule event date:', error, event);
        return false;
      }
    });
  }, [scheduleEvents, currentDate]);

  // Group schedule events by vet
  const scheduleByVet = useMemo(() => {
    const grouped: { [key: string]: VetAvailability[] } = {};

    // Initialize columns for each team member
    teamMembers.forEach(member => {
      grouped[member.id] = [];
    });

    // Group schedule events with deduplication by ID
    const seenIds = new Set<string>();
    todaySchedule.forEach(event => {
      if (event.vet_user_id) {
        // Check for duplicate IDs
        if (seenIds.has(event.id)) {
          console.warn('DUPLICATE EVENT DETECTED:', event.id, event);
          return; // Skip duplicate
        }
        seenIds.add(event.id);

        if (!grouped[event.vet_user_id]) {
          grouped[event.vet_user_id] = [];
        }
        grouped[event.vet_user_id].push(event);
      }
    });

    // Sort events by time within each group
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const timeA = parseISO(a.start_at).getTime();
        const timeB = parseISO(b.start_at).getTime();
        return timeA - timeB;
      });
    });

    return grouped;
  }, [todaySchedule, teamMembers]);

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    onNavigate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    onNavigate(newDate);
  };

  const goToToday = () => {
    onNavigate(new Date());
  };

  // Filter team members based on selection
  const filteredTeamMembers = useMemo(() => {
    if (selectedVetIds.size === 0) {
      return []; // No doctors selected = show no columns
    }
    return teamMembers.filter(member => selectedVetIds.has(member.id));
  }, [teamMembers, selectedVetIds]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, event: VetAvailability) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event.id);

    // Calculate where in the event box the user grabbed it
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    setDragOffset(offsetY);
  };

  const handleDragOver = (e: React.DragEvent, vetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverVet(vetId);
  };

  const handleDragLeave = () => {
    setDragOverVet(null);
  };

  const handleDrop = async (e: React.DragEvent, vetId: string) => {
    e.preventDefault();
    setDragOverVet(null);

    if (!draggedEvent) return;

    const event = draggedEvent;
    const dragStartTime = parseISO(event.start_at);

    // Get the grid container for this vet
    const gridContainer = gridRefs.current[vetId];
    if (!gridContainer) {
      console.error('No grid container found for vet:', vetId);
      return;
    }

    // Get the drop position relative to the entire grid (from top of 8 AM)
    const gridRect = gridContainer.getBoundingClientRect();
    // Adjust for where the user grabbed the event (subtract the offset)
    const adjustedOffsetY = (e.clientY - gridRect.top) - dragOffset;

    // Calculate total minutes from 8:00 AM based on pixel position
    const minutesFromGridTop = (adjustedOffsetY / SLOT_HEIGHT_PX) * SLOT_MINUTES;
    const rawMinutes = gridStartMin + minutesFromGridTop;

    // Snap to nearest slot boundary
    const snappedMinutes = Math.round(rawMinutes / SLOT_MINUTES) * SLOT_MINUTES;

    // Create new start time
    const newStartTime = new Date(currentDate);
    newStartTime.setHours(Math.floor(snappedMinutes / 60));
    newStartTime.setMinutes(snappedMinutes % 60);
    newStartTime.setSeconds(0);
    newStartTime.setMilliseconds(0);

    console.log('🎯 NEW TIME CALCULATION:', {
      currentDate: currentDate.toISOString(),
      snappedMinutes,
      newStartTime: newStartTime.toLocaleString(),
      willSendAsFormat: format(newStartTime, 'HH:mm')
    });

    // Calculate duration and end time
    const originalDuration = parseISO(event.end_at).getTime() - parseISO(event.start_at).getTime();
    const newEndTime = new Date(newStartTime.getTime() + originalDuration);

    // Update event if changed
    const timeChanged = newStartTime.getTime() !== dragStartTime.getTime();

    if (timeChanged && onRescheduleEvent) {
      // Check if this is a recurring event
      console.log('🔍 [WorkScheduleSwimLanesView] Checking if recurring:', {
        event_id: event.id,
        is_recurring: event.is_recurring,
        recurring_template_id: event.recurring_template_id,
        should_show_modal: event.is_recurring || event.recurring_template_id
      });

      if (event.is_recurring || event.recurring_template_id) {
        console.log('✅ [WorkScheduleSwimLanesView] Showing recurring modal');
        // Show modal to ask user what they want to do
        setPendingRecurringAction({ event, newStartTime, newEndTime });
        setRecurringModalAction('move');
        setShowRecurringModal(true);
      } else {
        console.log('❌ [WorkScheduleSwimLanesView] Not recurring, calling onRescheduleEvent directly');
        // Non-recurring event - just reschedule it
        await onRescheduleEvent(event.id, newStartTime, newEndTime);
      }
    }

    setDraggedEvent(null);
  };

  const handleDragEnd = () => {
    setDraggedEvent(null);
    setDragOverVet(null);
    setDragOffset(0);
  };

  const handleEditFromView = () => {
    // Close view modal and open edit modal directly (even for recurring events)
    setShowViewModal(false);

    if (!selectedEventForView) return;

    console.log('🖊️ [WorkScheduleSwimLanesView] Opening edit modal for event:', selectedEventForView.id);
    // Always open edit modal directly - the edit modal will handle showing recurring options on save
    onSelectEvent(selectedEventForView.id);
  };

  const handleDeleteFromView = () => {
    // Close view modal
    setShowViewModal(false);

    if (!selectedEventForView) return;

    // Check if this is a recurring event
    if (selectedEventForView.is_recurring || selectedEventForView.recurring_template_id) {
      console.log('✅ [WorkScheduleSwimLanesView] Showing recurring modal for delete');
      setPendingRecurringAction({ event: selectedEventForView });
      setRecurringModalAction('delete');
      setShowRecurringModal(true);
    } else {
      // For non-recurring events, show custom confirmation modal
      console.log('🗑️ [WorkScheduleSwimLanesView] Showing delete confirmation for non-recurring event');
      setShowConfirmDelete(true);
    }
  };

  const performNonRecurringDelete = async () => {
    if (!selectedEventForView) return;

    console.log('🗑️ [WorkScheduleSwimLanesView] Deleting non-recurring event:', selectedEventForView.id);
    setIsDeleting(true);

    try {
      const jwt_token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${selectedEventForView.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${jwt_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete event');
      }

      console.log('✅ [WorkScheduleSwimLanesView] Event deleted successfully');

      // Close modal and refresh
      setShowConfirmDelete(false);
      setSelectedEventForView(null);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('❌ [WorkScheduleSwimLanesView] Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle recurring event modal confirmation
  const handleRecurringModalConfirm = async (scope: 'single' | 'following' | 'all') => {
    if (!pendingRecurringAction) return;

    const { event, newStartTime, newEndTime } = pendingRecurringAction;
    const jwt_token = localStorage.getItem('token');
    const templateId = event.recurring_template_id;

    if (!templateId) {
      console.error('No template ID found for recurring event');
      return;
    }

    try {
      if (recurringModalAction === 'move' && newStartTime && newEndTime) {
        // Reschedule the event
        if (scope === 'single') {
          // Create an exception for just this date
          const instanceDate = format(parseISO(event.start_at), 'yyyy-MM-dd');

          // Send full ISO timestamps so backend knows the timezone
          console.log('📤 Sending modify-single request:', {
            instanceDate,
            newStartTime: newStartTime.toISOString(),
            newEndTime: newEndTime.toISOString()
          });

          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/modify-single?instance_date=${instanceDate}`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                new_start_at: newStartTime.toISOString(),
                new_end_at: newEndTime.toISOString(),
                new_availability_type: event.availability_type
              })
            }
          );
        } else if (scope === 'following') {
          // Split the template at this date
          const splitDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/split?split_date=${splitDate}&new_start_time=${format(newStartTime, 'HH:mm')}&new_end_time=${format(newEndTime, 'HH:mm')}`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } else if (scope === 'all') {
          // Update the template itself
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                start_time: format(newStartTime, 'HH:mm:ss'),
                end_time: format(newEndTime, 'HH:mm:ss'),
                availability_type: event.availability_type
              })
            }
          );
        }
        // Refresh the schedule data without losing context
        if (onRefresh) {
          await onRefresh();
        }
      } else if (recurringModalAction === 'edit') {
        // For edit, just open the edit modal (user will make changes there)
        onSelectEvent(event.id);
      } else if (recurringModalAction === 'delete') {
        if (scope === 'single') {
          // Delete this specific instance by adding EXDATE
          const instanceDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}/delete-single?instance_date=${instanceDate}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } else if (scope === 'following') {
          // End the template before this date
          const endDate = format(parseISO(event.start_at), 'yyyy-MM-dd');
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${jwt_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                effective_until: endDate
              })
            }
          );
        } else if (scope === 'all') {
          // Delete the entire template
          await fetch(
            `${API_BASE_URL}/api/v1/scheduling/recurring-availability/${templateId}`,
            {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${jwt_token}`
              }
            }
          );
        }
        // Refresh the schedule data without losing context
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error('Error handling recurring event action:', error);
    } finally {
      // Clean up and close modal
      setShowRecurringModal(false);
      setPendingRecurringAction(null);
    }
  };

  // Handlers for drag-to-create new availability blocks
  const handleSlotMouseDown = (vetId: string, slotTime: Date) => {
    if (draggedEvent) return; // Don't create when dragging an event

    setIsCreatingSlot(true);
    setSlotCreationStart({ vetId, slotTime });
    setSlotCreationEnd({ vetId, slotTime });
  };

  const handleSlotMouseEnter = (vetId: string, slotTime: Date) => {
    if (isCreatingSlot && slotCreationStart && slotCreationStart.vetId === vetId) {
      setSlotCreationEnd({ vetId, slotTime });
    }
  };

  const handleSlotMouseUp = () => {
    if (isCreatingSlot && slotCreationStart && slotCreationEnd && onSelectSlot) {
      const startMs = Math.min(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
      const endMs = Math.max(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
      const startTime = new Date(startMs);
      const endTime = new Date(endMs + SLOT_MINUTES * 60000); // End of the last slot

      onSelectSlot({
        start: startTime,
        end: endTime,
        vetId: slotCreationStart.vetId, // Pass the vet ID from the row that was clicked
      });
    }

    setIsCreatingSlot(false);
    setSlotCreationStart(null);
    setSlotCreationEnd(null);
  };

  // Check if a slot is in the creation range
  const isSlotInCreationRange = (vetId: string, slotTime: Date): boolean => {
    if (!isCreatingSlot || !slotCreationStart || !slotCreationEnd) return false;
    if (vetId !== slotCreationStart.vetId) return false;

    const slotTimeMs = slotTime.getTime();
    const startTimeMs = Math.min(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());
    const endTimeMs = Math.max(slotCreationStart.slotTime.getTime(), slotCreationEnd.slotTime.getTime());

    return slotTimeMs >= startTimeMs && slotTimeMs <= endTimeMs;
  };

  const { startMin: businessStartMin, endMinExclusive: businessEndMinExclusive } = useMemo(
    () => businessHoursForDate(practiceHours, currentDate),
    [practiceHours, currentDate]
  );
  const gridStartMin = hoursMode === '24h' ? 0 : businessStartMin;
  const gridEndMinExclusive = hoursMode === '24h' ? 24 * 60 : businessEndMinExclusive;

  const timeSlots = useMemo(
    () => generateTimeSlots(currentDate, gridStartMin, gridEndMinExclusive),
    [currentDate, gridStartMin, gridEndMinExclusive]
  );

  useEffect(() => {
    console.info('[WorkSchedule][HoursMode]', {
      mode: hoursMode,
      date: format(currentDate, 'yyyy-MM-dd'),
      businessStartMin,
      businessEndMinExclusive,
      gridStartMin,
      gridEndMinExclusive,
    });
  }, [hoursMode, currentDate, businessStartMin, businessEndMinExclusive, gridStartMin, gridEndMinExclusive]);

  if (loading) {
    return (
      <div className="p-6 bg-white flex items-center justify-center h-96">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    );
  }

  // Calculate overlapping event columns for side-by-side display
  const calculateEventColumns = (events: VetAvailability[]) => {
    if (events.length === 0) return new Map<string, { column: number; totalColumns: number }>();
    
    const eventColumns = new Map<string, { column: number; totalColumns: number }>();
    
    // Convert to time ranges for easier comparison
    const eventRanges = events.map(e => ({
      id: e.id,
      start: parseISO(e.start_at).getTime(),
      end: parseISO(e.end_at).getTime(),
    }));
    
    // For each event, find all overlapping events and assign columns
    for (const event of eventRanges) {
      // Find all events that overlap with this one
      const overlapping = eventRanges.filter(e => 
        e.start < event.end && e.end > event.start
      );
      
      if (overlapping.length <= 1) {
        // No overlaps, single column
        eventColumns.set(event.id, { column: 0, totalColumns: 1 });
        continue;
      }
      
      // Sort overlapping events by start time, then by id for consistency
      overlapping.sort((a, b) => {
        if (a.start !== b.start) return a.start - b.start;
        return a.id.localeCompare(b.id);
      });
      
      // Assign columns based on position in sorted order
      const myIndex = overlapping.findIndex(e => e.id === event.id);
      eventColumns.set(event.id, { 
        column: myIndex, 
        totalColumns: overlapping.length 
      });
    }
    
    // Second pass: ensure all overlapping events have the same totalColumns
    for (const event of eventRanges) {
      const overlapping = eventRanges.filter(e => 
        e.start < event.end && e.end > event.start
      );
      
      // Find the max totalColumns among overlapping events
      let maxCols = 1;
      for (const e of overlapping) {
        const info = eventColumns.get(e.id);
        if (info && info.totalColumns > maxCols) {
          maxCols = info.totalColumns;
        }
      }
      
      // Update all overlapping to use the same max
      for (const e of overlapping) {
        const current = eventColumns.get(e.id);
        if (current) {
          eventColumns.set(e.id, { ...current, totalColumns: maxCols });
        }
      }
    }
    
    console.log('[SideBySide] Event columns:', Object.fromEntries(eventColumns));
    
    return eventColumns;
  };

  // Calculate the position and size of a schedule event from the start of the day
  const getEventStyle = (event: VetAvailability, columnInfo?: { column: number; totalColumns: number }) => {
    // Backend returns UTC timestamps
    // parseISO will convert to browser's local time, which is what we want for display
    const eventStartTime = parseISO(event.start_at);
    const eventEndTime = parseISO(event.end_at);

    // Get hours and minutes in local time (after parseISO converts UTC to local)
    const eventHours = eventStartTime.getHours();
    const eventMinutes = eventStartTime.getMinutes();

    // Calculate total minutes from 8:00 AM start
    const minutesFromStart = (eventHours * 60 + eventMinutes) - gridStartMin;

    // Calculate duration
    const durationMinutes = (eventEndTime.getTime() - eventStartTime.getTime()) / 60000;

    // Convert to pixels
    const pixelsPerMinute = SLOT_HEIGHT_PX / SLOT_MINUTES;
    const topOffset = Math.round(minutesFromStart * pixelsPerMinute);
    const heightPx = Math.round(durationMinutes * pixelsPerMinute);

    // Calculate horizontal positioning for overlapping events
    if (columnInfo && columnInfo.totalColumns > 1) {
      const columnWidth = 100 / columnInfo.totalColumns;
      const leftPercent = columnInfo.column * columnWidth;
      return {
        top: `${topOffset}px`,
        height: `${heightPx}px`,
        left: `calc(${leftPercent}% + 2px)`,
        right: `calc(${100 - leftPercent - columnWidth}% + 2px)`,
        width: `calc(${columnWidth}% - 4px)`,
      };
    }

    return {
      top: `${topOffset}px`,
      height: `${heightPx}px`,
    };
  };

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Date Navigation Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          {/* Left spacer to balance the layout */}
          <div className="w-24"></div>

          {/* Center: Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={goToPreviousDay}
              className="p-2 hover:bg-gray-200 rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
            </h2>
            <button
              onClick={goToNextDay}
              className="p-2 hover:bg-gray-200 rounded-md transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Right: Today button */}
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-2"
          >
            <Calendar className="w-4 h-4" />
            Today
          </button>
        </div>
      </div>

      {/* Time-Slot Grid */}
      <div className="flex-1 overflow-auto">
        <div className="inline-flex min-w-full">
          {/* Time Column */}
          <div className="w-24 flex-shrink-0 border-r border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-primary">
            <div className="h-12 border-b border-gray-200 dark:border-dark-border sticky top-0 bg-gray-50 dark:bg-dark-bg-primary z-40 shadow-sm ring-1 ring-black/5 dark:ring-white/10"></div>
            {timeSlots.map((slot, idx) => (
              <div
                key={idx}
                className={[
                  'h-8 relative',
                  // Draw exactly ONE horizontal line per slot using border-top.
                  // This avoids doubled lines in 15-min mode.
                  idx === 0 || slot.time.getMinutes() === 0
                    ? 'border-t-2 border-gray-300 dark:border-gray-600'
                    : 'border-t border-gray-100 dark:border-dark-border',
                  idx === timeSlots.length - 1 ? 'border-b border-gray-100 dark:border-dark-border' : '',
                ].join(' ')}
              >
                {slot.label && (
                  <div className="absolute top-0 right-0 pr-3 flex items-center h-8">
                    <span className="text-xs text-gray-500 font-medium">{slot.label}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Vet Columns */}
          {filteredTeamMembers.map(member => {
            const memberSchedule = scheduleByVet[member.id] || [];

            return (
              <div key={member.id} className="flex-1 min-w-[250px] max-w-[400px] border-r border-gray-200 dark:border-dark-border relative">
                {/* Header */}
                <div className="h-12 border-b border-gray-200 dark:border-dark-border px-3 flex items-center bg-gray-50 dark:bg-dark-bg-primary sticky top-0 z-50 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      photoUrl={member.photo_url}
                      fullName={member.full_name || member.email}
                      size="sm"
                    />
                    <div>
                      <div className="font-semibold text-sm text-gray-900 dark:text-dark-text-primary">
                        {member.full_name || member.email.split('@')[0]}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {memberSchedule.length} blocks
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Slots Grid (background) */}
                <div
                  ref={(el) => { if (el) gridRefs.current[member.id] = el; }}
                  className="relative"
                  onDragOver={(e) => handleDragOver(e, member.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, member.id)}
                >
                  {timeSlots.map((slot, idx) => {
                    const isDropTarget = dragOverVet === member.id;
                    const isInCreationRange = isSlotInCreationRange(member.id, slot.time);
                    const minute = slot.time.getMinutes();
                    const borderClass = [
                      idx === 0 || minute === 0
                        ? 'border-t-2 border-gray-300 dark:border-gray-600'
                        : 'border-t border-gray-100 dark:border-dark-border',
                      idx === timeSlots.length - 1 ? 'border-b border-gray-100 dark:border-dark-border' : '',
                    ].join(' ');

                    return (
                      <div
                        key={idx}
                        className={`h-8 ${borderClass} transition-colors relative ${isInCreationRange
                            ? 'bg-blue-200 cursor-pointer'
                            : isDropTarget
                              ? 'bg-blue-50 cursor-pointer'
                          : 'bg-white hover:bg-gray-50 cursor-pointer'
                          }`}
                        onMouseDown={() => handleSlotMouseDown(member.id, slot.time)}
                        onMouseEnter={() => handleSlotMouseEnter(member.id, slot.time)}
                        onMouseUp={handleSlotMouseUp}
                      >
                      </div>
                    );
                  })}

                  {/* Schedule Events Overlay */}
                  <div className="absolute inset-0 pointer-events-none z-0">
                    {(() => {
                      // Calculate column positions for overlapping events
                      const eventColumns = calculateEventColumns(memberSchedule);
                      
                      return memberSchedule.map((event) => {
                        const columnInfo = eventColumns.get(event.id);
                        const style = getEventStyle(event, columnInfo);
                        const isDragging = draggedEvent?.id === event.id;
                        const doctorColor = getDoctorColor(event.vet_user_id, teamMembers);
                        const availabilityOpacity = getAvailabilityOpacity(event.availability_type);
                        const hasMultipleColumns = columnInfo && columnInfo.totalColumns > 1;

                        return (
                          <div
                            key={event.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, event)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              // On click, show view modal first
                              setSelectedEventForView(event);
                              setShowViewModal(true);
                            }}
                            className={`absolute rounded px-2 py-1 cursor-move hover:opacity-90 transition-opacity border-l-4 overflow-hidden pointer-events-auto text-white z-10 ${doctorColor.bg} ${doctorColor.border} ${isDragging ? 'opacity-50' : availabilityOpacity} ${hasMultipleColumns ? '' : 'left-1 right-1'}`}
                            style={style}
                          >
                            <div className="flex flex-col h-full justify-between">
                              {/* Title: use custom title if provided, otherwise fall back to availability type */}
                              <div className="text-xs font-semibold truncate">
                                {event.title || getAvailabilityLabel(event.availability_type)}
                              </div>
                              <div className="text-xs font-medium">
                                {format(parseISO(event.start_at), 'h:mm a')} - {format(parseISO(event.end_at), 'h:mm a')}
                              </div>
                              {event.notes && (
                                <div className="text-xs opacity-90 truncate">
                                  {event.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recurring Event Action Modal */}
      {/* View Event Modal */}
      <ViewEventModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        event={selectedEventForView}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
      />

      <RecurringEventActionModal
        isOpen={showRecurringModal}
        onClose={() => {
          setShowRecurringModal(false);
          setPendingRecurringAction(null);
        }}
        onConfirm={handleRecurringModalConfirm}
        actionType={recurringModalAction}
      />

      {/* Confirm Delete Modal (for non-recurring events) */}
      <ConfirmDeleteModal
        isOpen={showConfirmDelete}
        onClose={() => {
          setShowConfirmDelete(false);
          setIsDeleting(false);
        }}
        onConfirm={performNonRecurringDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default WorkScheduleSwimLanesView;

